import { Link } from 'react-router-dom';
import { ArrowRight, Recycle, Users, MapPin, Play } from 'lucide-react';
import { useRef, useState, useEffect, useCallback } from 'react';

// Check if connection is slow
function isSlowConnection(): boolean {
  if (typeof window === 'undefined') return false;
  const connection = (navigator as Navigator & { connection?: { effectiveType?: string; saveData?: boolean } }).connection;
  return !!(connection?.saveData || connection?.effectiveType === 'slow-2g' || connection?.effectiveType === '2g');
}

// Get appropriate video source based on screen width
function getVideoSource(): string {
  if (typeof window === 'undefined') return '/videos/nature-hero-720p.mp4';
  
  const width = window.innerWidth;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const effectiveWidth = width * dpr;
  
  if (width < 768) return '/videos/nature-hero-720p.mp4';
  if (effectiveWidth >= 2560) return '/videos/nature-hero-4k.mp4';
  if (effectiveWidth >= 1280) return '/videos/nature-hero-1080p.mp4';
  return '/videos/nature-hero-720p.mp4';
}

export function Hero() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  // Set mounted state after hydration
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Load video function - called when conditions are met
  const loadVideo = useCallback(() => {
    if (shouldLoadVideo) return; // Already triggered
    setShouldLoadVideo(true);
  }, [shouldLoadVideo]);

  // Trigger video load after idle callback or 4 seconds (whichever comes first)
  // This ensures LCP is painted before video starts loading
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    // Skip video on slow connections
    if (isSlowConnection()) {
      setVideoError(true);
      return;
    }

    // Use requestIdleCallback if available, otherwise setTimeout
    let timeoutId: ReturnType<typeof setTimeout>;
    let idleCallbackId: number | undefined;

    const triggerLoad = () => {
      loadVideo();
    };

    // Fallback timeout - load after 4 seconds max
    timeoutId = setTimeout(triggerLoad, 4000);

    // Try to use requestIdleCallback for better performance
    if ('requestIdleCallback' in window) {
      idleCallbackId = (window as Window & { requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback(triggerLoad, { timeout: 4000 });
    }

    return () => {
      clearTimeout(timeoutId);
      if (idleCallbackId && 'cancelIdleCallback' in window) {
        (window as Window & { cancelIdleCallback: (id: number) => void }).cancelIdleCallback(idleCallbackId);
      }
    };
  }, [loadVideo]);

  // Actually load the video when shouldLoadVideo becomes true
  useEffect(() => {
    if (!shouldLoadVideo) return;
    
    const video = videoRef.current;
    if (!video) return;

    // Set video source
    const videoSrc = getVideoSource();
    video.src = videoSrc;
    
    // Set a timeout - if video doesn't load in 5 seconds, show placeholder
    const timeoutId = setTimeout(() => {
      if (!video.readyState || video.readyState < 3) {
        setVideoError(true);
      }
    }, 5000);

    const handleCanPlay = () => {
      clearTimeout(timeoutId);
      setVideoLoaded(true);
      video.play().catch(() => {
        // Autoplay blocked - show placeholder
        setVideoError(true);
      });
    };

    const handleError = () => {
      clearTimeout(timeoutId);
      setVideoError(true);
    };

    video.addEventListener('canplaythrough', handleCanPlay);
    video.addEventListener('error', handleError);
    
    // Try to load
    video.load();

    return () => {
      clearTimeout(timeoutId);
      video.removeEventListener('canplaythrough', handleCanPlay);
      video.removeEventListener('error', handleError);
    };
  }, [shouldLoadVideo]);

  const scrollToServices = () => {
    const servicesSection = document.getElementById('services');
    if (servicesSection) {
      servicesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const stats = [
    { value: '100,000+', label: 'MT Processed Annually', icon: Recycle, color: 'from-eco-green-400 to-eco-green-500' },
    { value: '30+', label: 'Trusted Clients', icon: Users, color: 'from-eco-green-500 to-eco-green-600' },
    { value: '7 States', label: 'Pan-India Presence', icon: MapPin, color: 'from-eco-green-600 to-eco-green-700' },
  ];

  const showVideo = videoLoaded && !videoError;

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden -mt-24" id="hero">
      {/* Background - fixed aspect ratio to prevent CLS */}
      <div className="absolute inset-0" style={{ aspectRatio: '16/9', minHeight: '100vh' }}>
        {/* Placeholder image - always visible until video loads */}
        <img
          src="/images/hero-placeholder.webp"
          alt=""
          width={1920}
          height={1080}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${showVideo ? 'opacity-0' : 'opacity-100'}`}
          loading="eager"
          {...{ fetchpriority: 'high' } as React.ImgHTMLAttributes<HTMLImageElement>}
        />
        {/* Video - always render but hide with CSS if error, prevents hydration mismatch */}
        <video
          ref={videoRef}
          muted
          loop
          playsInline
          preload="none"
          width={1920}
          height={1080}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${showVideo ? 'opacity-100' : 'opacity-0'}`}
          style={{ display: videoError ? 'none' : undefined }}
        >
          <track kind="captions" src="" label="No captions available" default />
        </video>
        <div className="absolute inset-0 bg-black/30" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-20 pt-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/15 backdrop-blur-xl rounded-full mb-6 border border-white/25">
              <div className={isMounted ? 'animate-spin' : ''} style={{ animationDuration: '8s' }}>
                <Recycle className="w-4 h-4 text-eco-green-400" />
              </div>
              <span className="text-sm font-medium text-white">
                One Stop Circular Solution
              </span>
            </div>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-6 leading-[1.1]">
              Comprehensive{' '}
              <span className="relative inline-block">
                <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-eco-green-300 via-eco-green-400 to-eco-green-300">
                  Waste Management
                </span>
                <span className="absolute -bottom-2 left-0 right-0 h-3 bg-eco-green-500/30 rounded-full -z-10" />
              </span>{' '}
              Solutions
            </h1>

            {/* Sub-headline */}
            <div className="mb-8">
              <div className="flex items-center gap-4 flex-wrap justify-center lg:justify-start text-lg">
                {['Collection', 'Recycling', 'Co-processing', 'Compliance'].map((item) => (
                  <span
                    key={item}
                    className="flex items-center gap-2 text-eco-green-100"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-eco-green-400" />
                    {item}
                  </span>
                ))}
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Link
                to="/services"
                className="group px-8 py-4 bg-white text-eco-green-800 font-semibold rounded-2xl hover:bg-eco-green-50 hover:scale-[1.03] active:scale-[0.98] transition-all duration-300 flex items-center justify-center gap-3 shadow-2xl shadow-black/20"
              >
                <Play className="w-5 h-5" />
                Explore Services
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/contact?quote=true"
                className="group px-8 py-4 bg-white/10 backdrop-blur-md border border-white/25 text-white font-semibold rounded-2xl hover:bg-white/20 hover:scale-[1.03] active:scale-[0.98] transition-all duration-300 flex items-center justify-center gap-2"
              >
                Get Quote
              </Link>
            </div>
          </div>

          {/* Right Stats Cards */}
          <div className="space-y-4">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className="group bg-white/10 backdrop-blur-xl border border-white/20 shadow-xl shadow-black/20 rounded-2xl p-6 flex items-center gap-5 cursor-pointer hover:bg-white/10 hover:translate-x-[-5px] hover:scale-[1.02] transition-all"
              >
                <div className={`bg-gradient-to-br ${stat.color} p-4 rounded-xl shadow-lg group-hover:scale-110 transition-transform`}>
                  <stat.icon className="w-7 h-7 text-white" />
                </div>
                <div>
                  <p className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">{stat.value}</p>
                  <p className="text-eco-green-200 text-xs sm:text-sm font-medium">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <button
        onClick={scrollToServices}
        aria-label="Scroll to services section"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 cursor-pointer group"
      >
        <div className="flex flex-col items-center gap-2">
          <span className="text-xs uppercase tracking-widest text-white/60 group-hover:text-white transition-colors">Scroll to explore</span>
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2 group-hover:border-white/60 transition-colors">
            <div className={`w-1.5 h-1.5 bg-white rounded-full ${isMounted ? 'animate-bounce' : ''}`} />
          </div>
        </div>
      </button>
    </section>
  );
}
